import React, { useEffect, useState, useMemo } from 'react';
import client from '@/services/client';
import { CreditCard, RefreshCcw, Undo2, Loader2, X, IndianRupee, Zap, User, Search, Calendar } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PaymentsBrowser — shared table for viewing every Razorpay payment across the
 * platform, with filters (status, name/email search, date range) and aggregate
 * totals. Optionally shows a per-row Refund button (admins only).
 *
 * Props:
 *   endpoint       : API path (e.g. "/admin/payments" or "/support/payments")
 *   canRefund      : bool — show Refund column + modal (admins only)
 *   testIdPrefix   : string prefix for data-testids ("admin-payments" | "support-payments")
 */
export default function PaymentsBrowser({ endpoint = '/admin/payments', canRefund = false, testIdPrefix = 'payments' }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ total_amount_inr: 0, total_refunded_inr: 0, total_mu_credited: 0, total_mu_debited: 0 });
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [refundTarget, setRefundTarget] = useState(null);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (search.trim()) p.set('search', search.trim());
    if (startDate) p.set('start_date', startDate);
    if (endDate) p.set('end_date', endDate);
    p.set('limit', '200');
    return p.toString();
  }, [status, search, startDate, endDate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`${endpoint}?${queryParams}`);
      setRows(data.records || []);
      setTotal(data.total || 0);
      setTotals(data.totals || { total_amount_inr: 0, total_refunded_inr: 0, total_mu_credited: 0, total_mu_debited: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load payments');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, startDate, endDate]);

  const onSearchKey = (e) => { if (e.key === 'Enter') load(); };
  const clearFilters = () => { setStatus(''); setSearch(''); setStartDate(''); setEndDate(''); setTimeout(load, 0); };

  return (
    <div data-testid={`${testIdPrefix}-page`}>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-emerald-500" /> Razorpay Payments
        </h1>
        <button onClick={load} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid={`${testIdPrefix}-refresh`}>
          <RefreshCcw className="w-4 h-4" />
        </button>
      </div>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-4">
        {canRefund
          ? 'Every INR recharge across the platform. Issue a full or partial refund and Mu points are auto-debited at the original conversion rate.'
          : 'Every INR recharge across the platform. Read-only view for support agents.'}
      </p>

      {/* Totals for the CURRENT filter set */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <TotalCard label="Total paid" value={`₹${totals.total_amount_inr.toFixed(2)}`} tone="emerald" testId={`${testIdPrefix}-total-paid`} />
        <TotalCard label="Total refunded" value={`₹${totals.total_refunded_inr.toFixed(2)}`} tone="red" testId={`${testIdPrefix}-total-refunded`} />
        <TotalCard label="Mu credited" value={totals.total_mu_credited} suffix="Mu" tone="emerald" testId={`${testIdPrefix}-total-mu-credited`} />
        <TotalCard label="Mu debited (refund)" value={totals.total_mu_debited} suffix="Mu" tone="red" testId={`${testIdPrefix}-total-mu-debited`} />
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-3 mb-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {[
            { v: '', l: 'All' },
            { v: 'paid', l: 'Paid' },
            { v: 'partially_refunded', l: 'Partial refund' },
            { v: 'refunded', l: 'Refunded' },
          ].map((f) => (
            <button
              key={f.v || 'all'}
              onClick={() => setStatus(f.v)}
              className={`px-3 py-1 text-xs rounded-full border ${
                status === f.v
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white dark:bg-[#111114] text-slate-700 dark:text-zinc-300 border-slate-200 dark:border-zinc-800 hover:border-emerald-500/40'
              }`}
              data-testid={`${testIdPrefix}-filter-${f.v || 'all'}`}
            >
              {f.l}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-500 dark:text-zinc-500 self-center">{total} record{total === 1 ? '' : 's'}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Search name, email, or Razorpay id (press Enter)"
              className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg text-sm text-slate-900 dark:text-white"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500 pointer-events-none" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg text-sm text-slate-900 dark:text-white"
              data-testid={`${testIdPrefix}-start-date`}
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500 pointer-events-none" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg text-sm text-slate-900 dark:text-white"
              data-testid={`${testIdPrefix}-end-date`}
            />
          </div>
        </div>
        {(status || search || startDate || endDate) && (
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="text-xs text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white underline"
              data-testid={`${testIdPrefix}-clear-filters`}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Table with horizontal scroll */}
      {loading ? (
        <p className="text-slate-500 dark:text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading payments…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 dark:border-zinc-800 p-10 text-center text-slate-500 dark:text-zinc-500 text-sm">
          No payments found for these filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-zinc-800/60 bg-white dark:bg-[#111114]" data-testid={`${testIdPrefix}-table`}>
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-slate-50 dark:bg-[#0e0e10] text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider sticky top-0">
              <tr>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Role</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-right p-3">Refunded</th>
                <th className="text-right p-3">Mu credited</th>
                <th className="text-right p-3">Rate</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">When</th>
                {canRefund && <th className="text-right p-3"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-200 dark:border-zinc-800/60" data-testid={`${testIdPrefix}-row-${r.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center flex-shrink-0">
                        <User className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-slate-900 dark:text-white text-sm truncate max-w-[220px]">{r.user_name || r.user_email}</p>
                        <p className="text-slate-500 dark:text-zinc-500 text-[11px] truncate max-w-[220px]">{r.user_email} · {r.razorpay_payment_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider">{r.user_role || '—'}</td>
                  <td className="p-3 text-right text-slate-900 dark:text-white whitespace-nowrap">
                    <span className="inline-flex items-center gap-0.5"><IndianRupee className="w-3 h-3" />{r.amount_inr.toFixed(2)}</span>
                  </td>
                  <td className="p-3 text-right text-slate-600 dark:text-zinc-300 whitespace-nowrap">
                    {r.refunded_amount_inr > 0
                      ? <span className="inline-flex items-center gap-0.5 text-red-600 dark:text-red-400">−<IndianRupee className="w-3 h-3" />{r.refunded_amount_inr.toFixed(2)}</span>
                      : '—'}
                  </td>
                  <td className="p-3 text-right text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    <span className="inline-flex items-center gap-0.5">+{r.mu_points_credited} <Zap className="w-3 h-3" /></span>
                  </td>
                  <td className="p-3 text-right text-slate-500 dark:text-zinc-500 text-xs whitespace-nowrap">₹{r.conversion_rate_inr_per_mu}/Mu</td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                  <td className="p-3 text-right text-slate-500 dark:text-zinc-500 text-xs whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                  </td>
                  {canRefund && (
                    <td className="p-3 text-right">
                      {r.refundable_inr > 0 && r.status !== 'signature_failed' ? (
                        <button
                          onClick={() => setRefundTarget(r)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 text-xs font-medium"
                          data-testid={`${testIdPrefix}-refund-btn-${r.id}`}
                        >
                          <Undo2 className="w-3.5 h-3.5" /> Refund
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400 dark:text-zinc-600">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {refundTarget && canRefund && (
        <RefundModal
          payment={refundTarget}
          onClose={() => setRefundTarget(null)}
          onDone={() => { setRefundTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function TotalCard({ label, value, suffix = '', tone = 'emerald', testId }) {
  const toneCls = tone === 'red'
    ? 'from-red-500/5 to-red-500/10 border-red-500/20 text-red-700 dark:text-red-300'
    : 'from-emerald-500/5 to-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300';
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${toneCls} p-3`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider opacity-80 mb-1">{label}</p>
      <p className="text-xl font-semibold text-slate-900 dark:text-white">
        {value}{suffix && <span className="text-slate-500 dark:text-zinc-500 text-sm ml-1">{suffix}</span>}
      </p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { c: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', l: 'Paid' },
    partially_refunded: { c: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', l: 'Partial refund' },
    refunded: { c: 'bg-red-500/10 text-red-600 dark:text-red-400', l: 'Refunded' },
    signature_failed: { c: 'bg-slate-500/10 text-slate-500 dark:text-zinc-400', l: 'Sig fail' },
    created: { c: 'bg-slate-500/10 text-slate-500 dark:text-zinc-400', l: 'Created' },
    pending: { c: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', l: 'Pending' },
  };
  const s = map[status] || { c: 'bg-slate-500/10 text-slate-500 dark:text-zinc-400', l: status || '—' };
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${s.c}`}>{s.l}</span>;
}

function RefundModal({ payment, onClose, onDone }) {
  const [full, setFull] = useState(true);
  const [amount, setAmount] = useState(String(payment.refundable_inr));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const rate = Number(payment.conversion_rate_inr_per_mu) || 1;
  const amt = full ? payment.refundable_inr : Number(amount || 0);
  const muWillDebit = Math.max(0, Math.min(
    Math.round(amt / rate),
    payment.mu_points_credited - payment.refunded_mu,
  ));

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const body = { notes: notes.trim() };
      if (!full && Number(amount) > 0) body.amount_inr = Number(amount);
      const { data } = await client.post(`/admin/payments/${payment.id}/refund`, body);
      toast.success(`Refunded ₹${data.refund_amount_inr.toFixed(2)} · debited ${data.mu_debited} Mu`);
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Refund failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="admin-refund-modal">
      <div className="w-full max-w-md bg-white dark:bg-[#0f0f12] border border-slate-200 dark:border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-zinc-800">
          <h3 className="text-slate-900 dark:text-white font-semibold flex items-center gap-2">
            <Undo2 className="w-4 h-4 text-red-500" /> Refund payment
          </h3>
          <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid="admin-refund-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="rounded-lg bg-slate-50 dark:bg-black border border-slate-200 dark:border-zinc-800 p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-500">User</span><span className="text-slate-900 dark:text-white">{payment.user_email}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-500">Original amount</span><span className="text-slate-900 dark:text-white">₹{payment.amount_inr.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-500">Already refunded</span><span className="text-slate-900 dark:text-white">₹{payment.refunded_amount_inr.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-500">Refundable</span><span className="text-emerald-600 dark:text-emerald-400 font-medium">₹{payment.refundable_inr.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-500">Mu credited</span><span className="text-slate-900 dark:text-white">{payment.mu_points_credited} (already debited {payment.refunded_mu})</span></div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-zinc-300 cursor-pointer">
              <input type="radio" checked={full} onChange={() => setFull(true)} data-testid="admin-refund-full" />
              Full remaining refund (₹{payment.refundable_inr.toFixed(2)})
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-zinc-300 cursor-pointer">
              <input type="radio" checked={!full} onChange={() => setFull(false)} data-testid="admin-refund-partial" />
              Partial amount
            </label>
            {!full && (
              <div className="relative pl-6">
                <span className="absolute left-9 top-1/2 -translate-y-1/2 text-slate-500 dark:text-zinc-500 text-sm">₹</span>
                <input
                  type="number" min="0.01" step="0.01" max={payment.refundable_inr}
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="w-40 pl-8 pr-3 py-2 bg-white dark:bg-black border border-slate-200 dark:border-zinc-800 rounded-lg text-slate-900 dark:text-white text-sm focus:outline-none focus:border-red-500/40"
                  data-testid="admin-refund-amount"
                />
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-zinc-500">Notes (optional)</label>
            <input
              type="text" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={250}
              placeholder="e.g. Duplicate charge · user request"
              className="mt-1 w-full bg-white dark:bg-black border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
              data-testid="admin-refund-notes"
            />
          </div>

          <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 p-3 text-xs flex items-center justify-between">
            <span className="text-slate-600 dark:text-zinc-400">Mu that will be debited from the user</span>
            <span className="text-red-700 dark:text-red-300 font-semibold" data-testid="admin-refund-mu-preview">−{muWillDebit} Mu</span>
          </div>

          {error && <p className="text-xs text-red-600 dark:text-red-400" data-testid="admin-refund-error">{error}</p>}

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg bg-slate-100 dark:bg-zinc-900 hover:bg-slate-200 dark:hover:bg-zinc-800 text-slate-700 dark:text-zinc-300 text-sm font-medium"
              data-testid="admin-refund-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || (!full && (!(Number(amount) > 0)))}
              className="flex-1 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium flex items-center justify-center gap-1"
              data-testid="admin-refund-submit"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
              {submitting ? 'Refunding…' : 'Process refund'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
